// src/slices/chatSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { HubConnection } from "@microsoft/signalr";
import {
  ChatDataPayload,
  IChatMessage,
  IConversation,
  IUserChatData,
} from "../types";
import { IUser } from "../../types/types";
import { userApi } from "../api/user.api";
import { IChatAppointment } from "../types/user.types";
import { noDataLabel } from "../../utils/constants";

interface IChatState {
  isConnected: boolean;
  connection?: HubConnection;
  chatData: IUserChatData;
  allConnectedUsers: number[];
  showUserJoinedModal: undefined | IUser;
  callEnded: undefined | string;
  unSeenChatMessages: number;
}

const initialState: IChatState = {
  isConnected: false,
  chatData: { conversations: [], currentUserId: 0 },
  allConnectedUsers: [],
  callEnded: undefined,
  showUserJoinedModal: undefined,
  unSeenChatMessages: 0,
};

function mergeFullChatData(
  current: IUserChatData,
  payload: IUserChatData
): IUserChatData {
  const incoming = payload.conversations ?? [];
  const existing = current.conversations;

  if (incoming.length === 0) {
    return {
      conversations: [...existing],
      currentUserId: payload.currentUserId ?? current.currentUserId,
    };
  }

  const incomingIds = new Set(incoming.map((c) => c.userId));
  const merged: IConversation[] = incoming.map((inc) => {
    const prev = existing.find((e) => e.userId === inc.userId);
    return {
      ...prev,
      ...inc,
      appointment: inc.appointment ?? prev?.appointment,
      active: inc.active ?? prev?.active ?? false,
      hasStarted: inc.hasStarted ?? prev?.hasStarted ?? false,
      messages: inc.messages ?? prev?.messages ?? [],
    };
  });
  const onlyLocal = existing.filter((e) => !incomingIds.has(e.userId));
  return {
    conversations: [...merged, ...onlyLocal],
    currentUserId: payload.currentUserId ?? current.currentUserId,
  };
}

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setWsConnection(state, action: PayloadAction<HubConnection>) {
      state.connection = action.payload;
    },
    setConnected(state, action: PayloadAction<boolean>) {
      state.isConnected = action.payload;
    },
    setChatUsers(state, { payload }: { payload: IConversation[] }) {
      state.chatData.conversations = payload;
    },
    endCall(state, { payload }: { payload: string | undefined }) {
      state.callEnded = payload;
    },
    updateChatData(state, { payload }: { payload: ChatDataPayload }) {
      const countUnread = (conversations: IConversation[]) =>
        conversations.reduce((total, item) => {
          return total + (item.messages?.filter(
            (inner) => inner?.unRead === 1 && inner.receiverId !== item.userId
          )?.length ?? 0);
        }, 0);

      if (Array.isArray(payload)) {
        const nextConversations = state.chatData.conversations.map((u) => {
          const user = payload.find((us) => us.userId === u.userId);
          return { ...u, online: !!user?.online, messages: user?.messages ?? u.messages };
        });
        return {
          ...state,
          chatData: { ...state.chatData, conversations: nextConversations },
          unSeenChatMessages: countUnread(nextConversations),
        };
      }

      const next = mergeFullChatData(state.chatData, payload);
      return { ...state, chatData: next, unSeenChatMessages: countUnread(next.conversations) };
    },
    addMessage(state, { payload }: { payload: IChatMessage }) {
      state.unSeenChatMessages++;
      state.chatData.conversations = state.chatData.conversations.map((u) =>
        u.userId === payload.senderId || u.userId === payload.receiverId
          ? { ...u, messages: [...(u.messages ?? []), payload] }
          : u
      );
    },
    updateAppointmentStarted(
      state,
      { payload }: { payload: IConversation }
    ) {
      state.chatData.conversations = state.chatData.conversations.map((u) =>
        u.userId === payload.userId ? { ...u, hasStarted: true } : u
      );
    },
    setUserTyping(state, { payload }: { payload: IChatMessage }) {
      const usr = state.chatData.conversations.find((u) => u.userId === payload.senderId);
      if (usr) usr.isTyping = payload.content.split(":")[1] === "1";
    },
    setUserConnected(state, { payload }: { payload: number }) {
      const usr = state.chatData.conversations.find((u) => u.userId === payload);
      if (usr) usr.online = true;
    },
    setUserDisconnected(state, { payload }: { payload: number }) {
      const usr = state.chatData.conversations.find((u) => u.userId === payload);
      if (usr) usr.online = false;
    },
    setUnReadMessages(state, { payload }: { payload: number }) {
      state.unSeenChatMessages = payload;
    },
    setUnReadMessagesForUser(state, { payload }: { payload: number }) {
      const usr = state.chatData.conversations.find((u) => u.userId === payload);
      if (usr) usr.messages = usr.messages.map((msg) => ({ ...msg, unRead: 0 }));
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      userApi.endpoints.getChatAppointments.matchFulfilled,
      (state, { payload }: { payload: IChatAppointment[] }) => {
        const allConnectedUsers = [
          ...new Set(payload.map((appointment) => appointment.usersId)),
        ];
        state.allConnectedUsers = allConnectedUsers;
        allConnectedUsers.forEach((userId) => {
          const appointmentDetails = payload.find(
            (appointment) => appointment.usersId === userId
          );

          const activeAppointment = payload.find(
            (appointment) =>
              appointment.active && appointment.usersId === userId
          );
          const existingUserIndex = state.chatData.conversations.findIndex(
            (user) => user.userId === userId
          );

          if (existingUserIndex !== -1) {
            state.chatData.conversations[existingUserIndex].active =
              !!activeAppointment;
            state.chatData.conversations[existingUserIndex].appointment =
              activeAppointment;
            state.chatData.conversations[existingUserIndex].hasStarted =
              !!activeAppointment?.startTime;
            const patientGuid = appointmentDetails?.users?.guid;
            if (patientGuid) {
              state.chatData.conversations[existingUserIndex].guid = patientGuid;
            }
          } else {
            state.chatData.conversations.push({
              active: !!activeAppointment,
              userId: userId,
              fullName: appointmentDetails?.users?.fullName || noDataLabel,
              guid: appointmentDetails?.users?.guid,
              online: false,
              isTyping: false,
              hasStarted: !!activeAppointment?.startTime,
              messages: [],
              appointment: activeAppointment,
            });
          }
        });
      }
    );
  },
});
export const { actions } = chatSlice;
export const {
  setWsConnection,
  setConnected,
  endCall,
  addMessage,
  setUserTyping,
  updateChatData,
  setUserConnected,
  setUserDisconnected,
  setChatUsers,
} = chatSlice.actions;
export default chatSlice.reducer;
